/**
 * Compact one-line activity serialization for LLM prompts.
 * Preserves the SWA_ai field mix: id, name, status, dates, float, resources, codes, preds.
 */
function formatTs(value) {
	if (value == null || value === '' || value === 0) return '';
	if (typeof value === 'string' && !/^\d+$/.test(value)) return value;
	const n = Number(value);
	if (!n) return '';
	try {
		return new Date(n).toISOString().slice(0, 10);
	} catch (e) {
		return '';
	}
}

/** Map a SiteWalker activity row onto the compact AI task shape. */
export function activityToAiTask(activity) {
	if (!activity) return null;
	const codes = [];
	const props = activity.properties || {};
	Object.keys(props).forEach(key => {
		const p = props[key];
		if (!p) return;
		const label = p.shortName || p.value;
		if (label) codes.push(label);
	});
	const preds = (activity.predecessors || [])
		.map(p => p.taskCode || p.ID || p.TaskId || p.task_code)
		.filter(Boolean);
	const resources = (activity.resources && activity.resources.length)
		? activity.resources
		: (activity.resourceDetails || []).map(r => r.Resource).filter(Boolean);
	return {
		taskCode: activity.taskCode || String(activity.activityId || ''),
		name: activity.name || '',
		statusCode: activity.statusCode || '',
		actStart: formatTs(activity.actualStart || activity.actStart),
		actFinish: formatTs(activity.actualFinish || activity.actFinish),
		remainHrs: activity.remainingDuration != null ? activity.remainingDuration : activity.remainHrs,
		floatHrs: activity.totalFloat != null ? activity.totalFloat : activity.floatHrs,
		resources,
		codes,
		preds
	};
}

export function pickLowestFloatActivities(activities, cap) {
	const list = (activities || []).filter(Boolean);
	const sorted = [...list].sort((a, b) => {
		const af = Number(a.totalFloat != null ? a.totalFloat : a.floatHrs) || 0;
		const bf = Number(b.totalFloat != null ? b.totalFloat : b.floatHrs) || 0;
		if (af !== bf) return af - bf;
		return String(a.taskCode || '').localeCompare(String(b.taskCode || ''));
	});
	if (cap == null || cap <= 0) {
		return sorted;
	}
	return sorted.slice(0, cap);
}

export function notesFromActivities(activities) {
	const notes = [];
	(activities || []).forEach(activity => {
		const code = activity.taskCode || activity.activityId || '';
		const list = Array.isArray(activity.notes) ? activity.notes : [];
		list.forEach(n => {
			const text = (n && (n.note || n.task_memo || n)) ? String(n.note || n.task_memo || n).trim() : '';
			if (text) notes.push(`[${code}] ${text}`);
		});
	});
	return notes;
}

export function isHitoEmail(email) {
	return String(email || '').toLowerCase().endsWith('@hitomgmt.com');
}

export function serializeTaskForAi(task) {
	let info = `[${task.taskCode}] ${task.name} | Status: ${task.statusCode}`;
	if (task.actStart) info += ` | Start: ${task.actStart}`;
	if (task.actFinish) info += ` | Finish: ${task.actFinish}`;
	if (task.remainHrs) info += ` | Rem Dur: ${task.remainHrs}`;
	if (task.floatHrs != null && task.floatHrs !== '') {
		const days = Number(task.floatHrs) / 8;
		if (!Number.isNaN(days)) info += ` | Float: ${days}d`;
	}
	if (task.resources && task.resources.length) {
		info += ` | Res: ${task.resources.join(', ')}`;
	}
	if (task.codes && task.codes.length) {
		info += ` | Codes: ${task.codes.join(', ')}`;
	}
	if (task.preds && task.preds.length) {
		info += ` | Preds: ${task.preds.slice(0, 3).join(', ')}`;
	}
	return info;
}

export function extractTaskCodes(text) {
	if (!text) return [];
	const regex = /\[([a-zA-Z0-9.\-_]+)\]/g;
	const found = [];
	let match;
	while ((match = regex.exec(text)) !== null) {
		found.push(match[1]);
	}
	return [...new Set(found)];
}

export function matchTaskCodes(codes, tasks) {
	const byLower = {};
	(tasks || []).forEach(task => {
		if (task.taskCode) byLower[String(task.taskCode).toLowerCase()] = task.taskCode;
	});
	const matched = [];
	const unmatched = [];
	(codes || []).forEach(code => {
		const key = String(code).trim().toLowerCase();
		if (byLower[key]) matched.push(byLower[key]);
		else unmatched.push(code);
	});
	return { matched: [...new Set(matched)], unmatched };
}
